import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/navigation';

import { localeAtom } from '@core/Atoms/Locale/Locale.atom';
import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { Filter } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { useSyncedAtomRef } from '@core/Hooks/UseSyncedAtomRef';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import { useGetRoot } from '@search/Hooks/UseGetRoot';

import { filterUpdateStartedAtom } from 'Atoms/App/Plp/FilterUpdateStarted';
import { priceTypeAtom } from 'Atoms/Search/PriceTypeAtom';
import { applyFilterChanges } from 'Utils/SearchFilters/ApplyFiltersChange';

const FORCE_UI_REFRESH = 'fui';

const useUpdateFilters = () => {
  const root = useGetRoot();
  const { push } = useRouter();
  const setFiltersList = useSetAtom(filtersListAtom);
  const setFilterUpdateStarted = useSetAtom(filterUpdateStartedAtom);
  const locale = useAtomValue(localeAtom);

  const filtersListRef = useSyncedAtomRef(filtersListAtom);
  const priceTypeRef = useSyncedAtomRef(priceTypeAtom);

  const applyNewFilters = (updatedFilters: Filter[], navigateOpts?: { scroll: boolean }) => {
    let newUrl = createPathFromFilters(updatedFilters, locale, root);
    setFilterUpdateStarted(true);
    const shouldForceUiRefresh =
      !newUrl.includes('?') && root !== 'dealerships' && root !== 'locations';

    //NOTE: this is a hack to get around an issue where the PlpWrapper ui does not update when going from /path?withQuery to /path
    if (shouldForceUiRefresh) newUrl = `${newUrl}?${FORCE_UI_REFRESH}`;

    push(newUrl, navigateOpts || { scroll: false });
  };

  const updateFilters = (updates: Filter[], navigateOpts?: { scroll: boolean }) => {
    const unusedPriceFilters =
      priceTypeRef.current === 'monthly-price'
        ? ['price__lte', 'price__gte']
        : ['monthly-price__lte', 'monthly-price__gte'];

    const newFilters = applyFilterChanges(filtersListRef.current, updates);
    if (newFilters) {
      const updatedFilters = newFilters.filter(item => !unusedPriceFilters.includes(item.id));
      setFiltersList(updatedFilters);
      applyNewFilters(updatedFilters, navigateOpts);
    }
  };

  return {
    updateFilters,
  };
};

export { useUpdateFilters };
